import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Move, X } from 'lucide-react';
import {
  computeStoryCropBox,
  exportStoryCropBlob,
  readImageUrlDimensions,
  STORY_ASPECT,
} from './socialStoryImage';
import {
  gymSocialBtnPrimary,
  gymSocialBtnSecondary,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';

const FRAME_WIDTH = 280;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export default function GymSocialStoryCropEditor({
  imageUrl,
  open,
  onClose,
  onApply,
  applying = false,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [box, setBox] = useState(null);
  const [cropPos, setCropPos] = useState({ sx: 0, sy: 0 });
  const dragRef = useRef(null);
  const frameRef = useRef(null);

  const frameHeight = FRAME_WIDTH / STORY_ASPECT;
  const scale = box ? FRAME_WIDTH / box.cropW : 1;
  const displayW = box ? box.naturalW * scale : 0;
  const displayH = box ? box.naturalH * scale : 0;
  const imgLeft = box ? -cropPos.sx * scale : 0;
  const imgTop = box ? -cropPos.sy * scale : 0;

  useEffect(() => {
    if (!open || !imageUrl) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    readImageUrlDimensions(imageUrl)
      .then(({ width, height }) => {
        if (cancelled) {
          return;
        }
        const cropBox = computeStoryCropBox(width, height);
        setBox({
          naturalW: width,
          naturalH: height,
          ...cropBox,
        });
        setCropPos({ sx: cropBox.sx, sy: cropBox.sy });
      })
      .catch(() => {
        if (!cancelled) {
          onClose?.();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, imageUrl, onClose]);

  const updateCropFromPointer = useCallback(
    (clientX, clientY) => {
      const drag = dragRef.current;
      if (!drag || !box) {
        return;
      }
      const dx = clientX - drag.startX;
      const dy = clientY - drag.startY;
      let sx = drag.startSx;
      let sy = drag.startSy;
      if (box.panAxis === 'x') {
        sx = clamp(drag.startSx - dx / scale, box.minSx, box.maxSx);
      } else if (box.panAxis === 'y') {
        sy = clamp(drag.startSy - dy / scale, box.minSy, box.maxSy);
      }
      setCropPos({ sx, sy });
    },
    [box, scale]
  );

  const onPointerDown = (e) => {
    if (!box || applying) {
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startSx: cropPos.sx,
      startSy: cropPos.sy,
    };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) {
      return;
    }
    updateCropFromPointer(e.clientX, e.clientY);
  };

  const onPointerUp = (e) => {
    if (dragRef.current) {
      dragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
  };

  const handleApply = async () => {
    if (!box || applying) {
      return;
    }
    try {
      const blob = await exportStoryCropBlob(imageUrl, {
        sx: cropPos.sx,
        sy: cropPos.sy,
        cropW: box.cropW,
        cropH: box.cropH,
      });
      await onApply?.(blob);
    } catch {
      /* parent shows toast */
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70"
      role="dialog"
      aria-modal="true"
      aria-labelledby="story-crop-title"
    >
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-xl dark:border-white/15 dark:bg-gray-900 p-4 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 id="story-crop-title" className={`text-base font-semibold ${gymSocialTitleText}`}>
              {t('gym.social.posts.storyCropEditorTitle')}
            </h3>
            <p className={`text-xs mt-1 flex items-center gap-1 ${gymSocialMutedText}`}>
              <Move className="h-3.5 w-3.5 shrink-0" />
              {t('gym.social.posts.storyCropDragHint')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={applying}
            className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10"
            aria-label={t('gym.social.posts.storyCropCancel')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mx-auto" style={{ width: FRAME_WIDTH }}>
          <div
            ref={frameRef}
            className="relative overflow-hidden rounded-[1.25rem] border-[6px] border-gray-900 bg-black shadow-lg touch-none select-none cursor-grab active:cursor-grabbing"
            style={{ width: FRAME_WIDTH, height: frameHeight }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-white/80" />
              </div>
            )}
            {box && !loading && (
              <img
                src={imageUrl}
                alt=""
                draggable={false}
                className="absolute max-w-none pointer-events-none"
                style={{
                  width: displayW,
                  height: displayH,
                  left: imgLeft,
                  top: imgTop,
                }}
              />
            )}
            <div
              className="absolute inset-0 pointer-events-none ring-1 ring-inset ring-white/25"
              aria-hidden
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            disabled={applying}
            onClick={onClose}
            className={gymSocialBtnSecondary}
          >
            {t('gym.social.posts.storyCropCancel')}
          </button>
          <button
            type="button"
            disabled={applying || loading || !box}
            onClick={handleApply}
            className={gymSocialBtnPrimary}
          >
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('gym.social.posts.storyCropApply')}
          </button>
        </div>
      </div>
    </div>
  );
}
